import { useState, useEffect, useContext } from "react";
import { DataContext } from "../../contexts/DataProvider";
import { useAlert } from "../../functions/useAlert";
import {
  fetchUsersList,
  updateUser,
  deleteUser,
  resendUserInvite,
  inviteUser,
  createUsers,
  fetchProratedAmount as fetchProratedAmountService,
} from "../../services/users.service";
import {
  EMAIL_REGEX,
  PHONE_REGEX,
  PASSWORD_REGEX,
  VAL_MSG,
  isValidPhone,
  isStateA,
} from "../utils/validation";

const defaultUserEntry = () => ({
  firstName: "",
  lastName: "",
  email: "",
  role: "user",
  status: "active",
  password: "",
  phone: "",
  mobile: "",
  manualPassword: false,
});

interface UseUsersManagementOptions {
  organisationList: any[];
  // "" or "All Organisation" means no filter — a global admin sees every org's users,
  // anyone else is scoped server-side to their own org regardless of this value.
  selectedOrganization?: string;
  onUserCreated?: () => void;
}

export function useUsersManagement({
  organisationList,
  selectedOrganization,
  onUserCreated,
}: UseUsersManagementOptions) {
  const { login }: any = useContext(DataContext);
  const [setAlert] = useAlert();

  // User list & search
  const [users, setUsers] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [optionsForUsers, setOptionsForUsers] = useState<any[]>([]);
  const [openUserRow, setOpenUserRow] = useState<number | null>(null);
  const [searchUserText, setSearchUserText] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [searchUser, setSearchUser] = useState<string>("");
  const [userSearchText, setUserSearchText] = useState<string>("");

  // Validation
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [userErrors, setUserErrors] = useState<Record<string, string>>({});

  // Password modal
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordModalUserId, setPasswordModalUserId] = useState<string | null>(null);
  const [passwordModalValues, setPasswordModalValues] = useState<{ password: string; confirm: string }>({ password: "", confirm: "" });
  const [passwordModalError, setPasswordModalError] = useState<string>("");

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
  const [deleteUserName, setDeleteUserName] = useState<string>("");

  // Confirm passwords (inline row edits)
  const [confirmPasswords, setConfirmPasswords] = useState<Record<string, string>>({});

  // Single user form (edit or add)
  const [form, setForm] = useState<Record<string, any>>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phone: "",
    mobile: "",
    orgId: "",
    role: "",
    status: "active",
    twoFactorEnabled: false,
  });
  const [openModalUser, setOpenModalForUser] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [addUserError, setAddUserError] = useState("");

  // Multi-user batch add
  const [userForms, setUserForms] = useState<any[]>([defaultUserEntry()]);
  const [userFormsErrors, setUserFormsErrors] = useState<Record<string, string>[]>([{}]);
  const [emailDuplicateWarnings, setEmailDuplicateWarnings] = useState<string[]>([""]);
  const [expandedUserIndices, setExpandedUserIndices] = useState<Set<number>>(new Set([0]));
  const [manualPassword, setManualPassword] = useState(false);
  const [showAddUserPassword, setShowAddUserPassword] = useState(false);
  const [userSubmitting, setUserSubmitting] = useState(false);

  // Seat confirmation (paid orgs)
  const [confirmSeatModal, setConfirmSeatModal] = useState(false);
  const [confirmSeatModalError, setConfirmSeatModalError] = useState("");
  const [confirmSeatLoading, setConfirmSeatLoading] = useState(false);

  // Billing
  const [proratedAmount, setProratedAmount] = useState<any>(null);
  const [isPremium, setIsPremium] = useState<{
    billingStatusCode: number;
    isPremium: boolean;
    headerText?: string;
    buttonText?: string;
  }>({ billingStatusCode: 0, isPremium: false, headerText: "", buttonText: "" });

  const manualSeatCount = userForms.filter((f) => f.manualPassword).length;

  // ─── Card helpers ─────────────────────────────────────────────────────────

  const isUserCardExpanded = (idx: number) => expandedUserIndices.has(idx);

  const openUserCard = (idx: number) =>
    setExpandedUserIndices((prev) => new Set(prev).add(idx));

  const toggleUserCard = (idx: number) =>
    setExpandedUserIndices((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });

  const addUserEntry = () => {
    const newIndex = userForms.length;
    setUserForms((prev) => [...prev, defaultUserEntry()]);
    setUserFormsErrors((prev) => [...prev, {}]);
    setEmailDuplicateWarnings((prev) => [...prev, ""]);
    openUserCard(newIndex);
  };

  const removeUserEntry = (index: number) => {
    setUserForms((prev) => prev.filter((_, i) => i !== index));
    setUserFormsErrors((prev) => prev.filter((_, i) => i !== index));
    setEmailDuplicateWarnings((prev) => prev.filter((_, i) => i !== index));
    setExpandedUserIndices((prev) => {
      const next = new Set<number>();
      prev.forEach((i) => {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      });
      return next;
    });
  };

  const updateUserEntry = (index: number, field: string, value: any) => {
    setUserForms((prev) => prev.map((f, i) => (i === index ? { ...f, [field]: value } : f)));
    setUserFormsErrors((prev) => prev.map((e, i) => (i === index ? { ...e, [field]: "" } : e)));
  };

  const checkEmailDuplicate = (index: number, email: string) => {
    if (!email || !EMAIL_REGEX.test(email)) {
      setEmailDuplicateWarnings((prev) => prev.map((w, i) => (i === index ? "" : w)));
      return;
    }
    const lower = email.toLowerCase();
    const inBatch = userForms.some((f, i) => i !== index && f.email.toLowerCase() === lower);
    if (inBatch) {
      setEmailDuplicateWarnings((prev) => prev.map((w, i) => (i === index ? "Already entered above" : w)));
      return;
    }
    const inSystem = users.some((u) => u.email?.toLowerCase() === lower);
    if (inSystem) {
      setEmailDuplicateWarnings((prev) => prev.map((w, i) => (i === index ? "Email already exists in the system" : w)));
      return;
    }
    setEmailDuplicateWarnings((prev) => prev.map((w, i) => (i === index ? "" : w)));
  };

  // ─── API helpers ──────────────────────────────────────────────────────────

  const fetchUsers = async () => {
    const orgIdFilter =
      selectedOrganization && selectedOrganization !== "All Organisation"
        ? selectedOrganization
        : undefined;
    const user_data: any = await fetchUsersList(orgIdFilter);
    const usersList = user_data?.users || [];
    setUsers(usersList);
    setOptionsForUsers(usersList);
    setAllUsers(usersList);
  };

  const fetchProratedAmount = async (orgId: string, count = 1) => {
    try {
      if (!orgId) { setProratedAmount(null); return; }
      const resp: any = await fetchProratedAmountService(orgId, count);
      setProratedAmount(resp?.prorated || null);
    } catch (err) {
      setProratedAmount(null);
      console.error("Error fetching prorated amount:", err);
    }
  };

  const sendInvites = async (entries: any[], orgId: string): Promise<{ successCount: number; firstError: string }> => {
    let successCount = 0;
    let firstError = "";
    for (const entry of entries) {
      try {
        const res: any = await inviteUser({
          firstName: entry.firstName,
          lastName: entry.lastName,
          email: entry.email,
          role: entry.role,
          orgId,
        });
        if (!res?.hasError) successCount++;
        else if (!firstError) firstError = res.message || "Failed to send invitation.";
      } catch (err: any) {
        if (!firstError) firstError = err?.data?.message || err?.message || "Failed to send invitation.";
      }
    }
    if (successCount > 0) setAlert(`${successCount} invitation(s) sent.`, "success");
    return { successCount, firstError };
  };

  const createUsersImmediate = async (orgId?: string) => {
    setUserSubmitting(true);
    const resolvedOrgId = orgId || form.orgId || (login?.data?.role === "orgadmin" ? login?.data?.orgId : "");
    const inviteEntries = userForms.filter((e) => !e.manualPassword);
    const manualEntries = userForms.filter((e) => e.manualPassword);
    try {
      if (inviteEntries.length > 0) await sendInvites(inviteEntries, resolvedOrgId);
      if (manualEntries.length > 0) {
        const body = manualEntries.map(({ manualPassword: _mp, ...entry }) => ({
          ...entry,
          orgId: resolvedOrgId,
          status: "active",
        }));
        const response: any = await createUsers(body);
        if (response?.error || response?.status >= 400) {
          const msg = response?.error || "Error creating users. Please try again.";
          if (msg.includes("already exist") || msg.includes("duplicate key")) {
            setAddUserError("One or more email addresses already exist.");
            setAlert("One or more email addresses already exist.", "error");
          } else {
            setAddUserError(msg);
          }
          return;
        }
        const count = response?.users?.length ?? manualEntries.length;
        setAlert(
          inviteEntries.length > 0
            ? `${count} user(s) created and ${inviteEntries.length} invitation(s) sent.`
            : `${count} user(s) created successfully.`,
          "success",
        );
      } else {
        setAlert(`${inviteEntries.length} invitation(s) sent.`, "success");
      }
      fetchUsers();
      setOpenModalForUser(false);
    } catch (error: any) {
      if (error?.status === 402) {
        setOpenModalForUser(false);
      } else {
        setAddUserError("Error creating users. Please try again.");
        console.error("Error saving users:", error);
      }
    } finally {
      setUserSubmitting(false);
    }
  };

  // ─── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization]);

  useEffect(() => {
    if (form.orgId) {
      const org = organisationList?.find((o: any) => o._id === form.orgId);
      const isPaid = org?.billingStatus === "active" && !org?.isBillingExempt;
      if (isPaid) {
        const billable = editingUser ? 1 : userForms.filter((f) => f.manualPassword).length;
        if (billable > 0) fetchProratedAmount(form.orgId, billable);
        else setProratedAmount(null);
      } else {
        setProratedAmount(null);
      }
    }
  }, [form.orgId]);

  useEffect(() => {
    if (!editingUser && openModalUser) {
      const orgId = form.orgId || (login?.data?.role === "orgadmin" ? login?.data?.orgId : "");
      if (orgId) {
        const org = organisationList?.find((o: any) => o._id === orgId);
        const isPaid = org?.billingStatus === "active" && !org?.isBillingExempt;
        if (isPaid) fetchProratedAmount(orgId, userForms.length);
        else setProratedAmount(null);
      }
    }
  }, [userForms.length]);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleUserInputChange = (userId: string, field: string, value: string | boolean) => {
    const idx = users.findIndex((u) => u._id === userId);
    if (idx === -1) { console.error(`User with ID ${userId} not found.`); return; }
    const updated = [...users];
    updated[idx] = { ...updated[idx], [field]: value };
    setUsers(updated);
  };

  const handleSaveUser = async (userId: string) => {
    try {
      const userToUpdate = users.find((user) => user._id === userId);
      if (!userToUpdate) return;
      if (userToUpdate.firstName && !/[a-zA-Z]/.test(userToUpdate.firstName)) { setAlert("First name cannot be purely numerical", "error"); return; }
      if (userToUpdate.lastName && !/[a-zA-Z]/.test(userToUpdate.lastName)) { setAlert("Last name cannot be purely numerical", "error"); return; }
      if (userToUpdate.email && !EMAIL_REGEX.test(userToUpdate.email)) { setAlert(VAL_MSG.email, "error"); return; }
      if (userToUpdate.phone && !isValidPhone(userToUpdate.phone)) { setAlert("Enter a valid phone number (e.g. +61412345678 or 0412345678)", "error"); return; }
      if (userToUpdate.mobile && !isValidPhone(userToUpdate.mobile)) { setAlert("Enter a valid mobile number (e.g. +61412345678 or 0412345678)", "error"); return; }
      const payload = { ...userToUpdate };
      delete payload.password;
      await updateUser(payload._id, payload);
      setAlert("User edited successfully!", "success");
      fetchUsers();
      setOpenUserRow(null);
    } catch (err) {
      console.error("Error updating user:", err);
      setAlert("Error updating user", "error");
    }
  };

  const handleSetPassword = async () => {
    if (!passwordModalUserId) { setPasswordModalError("No user selected."); return; }
    if (!passwordModalValues.password || !passwordModalValues.confirm) { setPasswordModalError("Both fields are required."); return; }
    if (passwordModalValues.password !== passwordModalValues.confirm) { setPasswordModalError("Passwords do not match."); return; }
    if (!PASSWORD_REGEX.test(passwordModalValues.password)) { setPasswordModalError(VAL_MSG.password); return; }
    try {
      const userToUpdate = users.find((u) => u._id === passwordModalUserId);
      if (!userToUpdate) throw new Error("User not found.");
      await updateUser(passwordModalUserId, { ...userToUpdate, password: passwordModalValues.password });
      setAlert("Password updated successfully!", "success");
      fetchUsers();
      setPasswordModalOpen(false);
    } catch (err: any) {
      console.error("Error updating password:", err);
      setAlert(err.message || "Error updating password", "error");
    } finally {
      setPasswordModalError("");
      setPasswordModalUserId(null);
      setPasswordModalValues({ password: "", confirm: "" });
    }
  };

  const handleExpandUser = (index: number) =>
    setOpenUserRow((prev) => (prev === index ? null : index));

  const handleSearchUser = (value: string) => {
    setSearchUser(value);
    if (!value.trim()) { setOptionsForUsers([]); setUsers(allUsers); return; }
    const filtered = allUsers.filter((user) =>
      user.firstName?.toLowerCase().includes(value.toLowerCase()),
    );
    setOptionsForUsers(filtered.map((user) => ({ label: user.firstName, value: user.firstName })));
    setUsers(filtered);
  };

  const handleOpenModal = (user: any = null) => {
    setEditingUser(user);
    if (user) {
      setForm({ ...user, password: "" });
    } else {
      setForm({
        firstName: "", lastName: "", email: "", password: "",
        phone: "", mobile: "",
        orgId: login?.data?.role === "orgadmin" ? login.data.orgId : "",
        role: login?.data?.role === "orgadmin" ? "user" : "",
        status: "active", twoFactorEnabled: false,
      });
      const initOrgId = login?.data?.role === "orgadmin" ? login.data.orgId : "";
      setUserForms([{ ...defaultUserEntry(), orgId: initOrgId }]);
      setUserFormsErrors([{}]);
      setEmailDuplicateWarnings([""]);
      setExpandedUserIndices(new Set([0]));
      if (initOrgId) fetchProratedAmount(initOrgId, 1);
    }
    setManualPassword(false);
    setAddUserError("");
    setOpenModalForUser(true);
  };

  const handleChangeForUser = async (field: string, value: any) => {
    setAddUserError("");
    setForm((prevForm) => {
      const updatedForm = { ...prevForm, [field]: value };
      if (field === "orgId") {
        const selectedOrg = organisationList.find((org: any) => org._id === value);
        if (selectedOrg) {
          updatedForm.twoFactorEnabled = selectedOrg.twoFactorEnabled;
          updatedForm.billingStatus = selectedOrg.billingStatus;
        }
        const billableCount = editingUser ? 1 : userForms.filter((f) => f.manualPassword).length;
        if (billableCount > 0) fetchProratedAmount(value, billableCount);
        else setProratedAmount(null);
      }
      return updatedForm;
    });
  };

  const validateUserEntry = (entry: any) => {
    const errs: Record<string, string> = {};
    if (!entry.firstName) errs.firstName = VAL_MSG.required;
    if (!entry.lastName) errs.lastName = VAL_MSG.required;
    if (!entry.email) errs.email = VAL_MSG.required;
    else if (!EMAIL_REGEX.test(entry.email)) errs.email = VAL_MSG.email;
    if (!entry.role) errs.role = VAL_MSG.required;
    if (entry.manualPassword && !entry.password) errs.password = VAL_MSG.required;
    if (entry.manualPassword && entry.password && !PASSWORD_REGEX.test(entry.password)) errs.password = VAL_MSG.password;
    if (entry.phone && !PHONE_REGEX.test(entry.phone)) errs.phone = "Enter a valid phone number";
    if (entry.mobile && !PHONE_REGEX.test(entry.mobile)) errs.mobile = "Enter a valid mobile number";
    return errs;
  };

  const handleSubmitUser = async () => {
    // ── EDIT MODE ────────────────────────────────────────────────────────────
    if (editingUser) {
      const errors: Record<string, string> = {};
      ["orgId", "firstName", "lastName", "email", "role", "status"].forEach((field) => {
        if (!form[field]) errors[field] = VAL_MSG.required;
      });
      if (form.firstName && !/[a-zA-Z]/.test(form.firstName.trim())) errors.firstName = "First name cannot be purely numerical";
      if (form.lastName && !/[a-zA-Z]/.test(form.lastName.trim())) errors.lastName = "Last name cannot be purely numerical";
      if (form.email && !EMAIL_REGEX.test(form.email)) errors.email = VAL_MSG.email;
      if (form.phone && !PHONE_REGEX.test(form.phone)) errors.phone = "Enter a valid phone number (e.g. +61412345678 or 0412345678)";
      if (form.mobile && !PHONE_REGEX.test(form.mobile)) errors.mobile = "Enter a valid mobile number (e.g. +61412345678 or 0412345678)";
      if (form.password && !PASSWORD_REGEX.test(form.password)) errors.password = VAL_MSG.password;
      setUserErrors(errors);
      if (Object.keys(errors).length > 0) return;
      setUserSubmitting(true);
      try {
        const response: any = await updateUser(editingUser._id, form);
        if (response?.error || response?.status >= 400) { setAddUserError("Error updating user. Please try again."); return; }
        setAlert("User updated successfully.", "success");
        fetchUsers();
        setOpenModalForUser(false);
      } catch {
        setAddUserError("Error updating user. Please try again.");
      } finally {
        setUserSubmitting(false);
      }
      return;
    }

    // ── ADD MODE ─────────────────────────────────────────────────────────────
    const orgId = form.orgId || (login?.data?.role === "orgadmin" ? login?.data?.orgId : "");
    if (!orgId) { setAddUserError("Please select an organisation."); return; }

    const allErrors = userForms.map((entry) => validateUserEntry(entry));
    setUserFormsErrors(allErrors);
    const hasErrors = allErrors.some((e) => Object.keys(e).length > 0);
    if (hasErrors) {
      const firstBad = allErrors.findIndex((e) => Object.keys(e).length > 0);
      openUserCard(firstBad);
      return;
    }

    const liveWarnings = userForms.map((entry, idx) => {
      if (!entry.email || !EMAIL_REGEX.test(entry.email)) return "";
      const lower = entry.email.toLowerCase();
      const inBatch = userForms.some((f, i) => i !== idx && f.email.toLowerCase() === lower);
      if (inBatch) return "Already entered above";
      const inSystem = users.some((u: any) => u.email?.toLowerCase() === lower);
      if (inSystem) return "Email already exists in the system";
      return "";
    });
    setEmailDuplicateWarnings(liveWarnings);
    if (liveWarnings.some((w) => !!w)) {
      const firstBad = liveWarnings.findIndex((w) => !!w);
      openUserCard(firstBad);
      setAlert("Please resolve duplicate email addresses before submitting.", "error");
      return;
    }

    setUserSubmitting(true);
    try {
      const inviteEntries = userForms.filter((e) => !e.manualPassword);
      const manualEntries = userForms.filter((e) => e.manualPassword);
      const selectedOrg = organisationList.find((org: any) => org._id === orgId);

      if (manualEntries.length === 0) {
        const { successCount, firstError } = await sendInvites(inviteEntries, orgId);
        if (successCount > 0) { fetchUsers(); setOpenModalForUser(false); }
        else { setAddUserError(firstError || "Failed to send invitations. Please try again."); }
        return;
      }

      if (selectedOrg?.isBillingExempt || isStateA(selectedOrg)) {
        await createUsersImmediate(orgId);
        onUserCreated?.();
        return;
      }

      if (selectedOrg?.billingStatus === "active" && !selectedOrg?.isBillingExempt) {
        await fetchProratedAmount(orgId, userForms.length);
        setConfirmSeatModal(true);
        return;
      }

      await createUsersImmediate(orgId);
      onUserCreated?.();
    } finally {
      setUserSubmitting(false);
    }
  };

  const handleConfirmSeatAddition = async () => {
    setConfirmSeatModalError("");
    setConfirmSeatLoading(true);
    try {
      const orgId = form.orgId || (login?.data?.role === "orgadmin" ? login?.data?.orgId : "");
      const inviteEntries = userForms.filter((e) => !e.manualPassword);
      const manualEntries = userForms.filter((e) => e.manualPassword);

      if (inviteEntries.length > 0) await sendInvites(inviteEntries, orgId);

      if (manualEntries.length === 0) {
        fetchUsers();
        setConfirmSeatModal(false);
        setOpenModalForUser(false);
        setConfirmSeatLoading(false);
        return;
      }
      const body = manualEntries.map(({ manualPassword: _mp, ...entry }) => ({ ...entry, orgId, status: "active" }));
      const response: any = await createUsers(body);
      if (response?.status === 402 || response?.error?.includes("402")) {
        setConfirmSeatModalError("Payment Failed: Card was declined. No users were created. Please check your payment method.");
        setConfirmSeatLoading(false);
        return;
      }
      if (response?.error || response?.status >= 400) {
        setConfirmSeatModalError(response?.error || "Error creating users. Please try again.");
        setConfirmSeatLoading(false);
        return;
      }
      fetchUsers();
      setConfirmSeatModal(false);
      setOpenModalForUser(false);
      setConfirmSeatLoading(false);
    } catch (error: any) {
      if (error?.response?.status === 402) {
        setConfirmSeatModalError("Payment Failed: Card was declined. No users were created. Please check your payment method.");
      } else {
        setConfirmSeatModalError("Error creating users. Please try again.");
      }
      console.error("Error saving users:", error);
      setConfirmSeatLoading(false);
    }
  };

  const handleResendInvite = async (userId: string) => {
    try {
      const res: any = await resendUserInvite(userId);
      if (res?.hasError) setAlert(res.message || "Failed to resend invitation.", "error");
      else setAlert("Invitation email resent successfully.", "success");
    } catch (err: any) {
      setAlert(err?.data?.message || "Failed to resend invitation.", "error");
    }
  };

  const handleDeleteUser = (id: string) => {
    const user = users.find((u: any) => u._id === id);
    setDeleteUserName(user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "");
    setDeleteUserId(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!deleteUserId) return;
    try {
      await deleteUser(deleteUserId);
      setUsers((prev) => prev.filter((item: any) => item._id !== deleteUserId));
      setAlert("User deleted successfully!", "success");
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      setAlert("Error deleting user", "error");
    } finally {
      setDeleteConfirmOpen(false);
      setDeleteUserId(null);
      setDeleteUserName("");
    }
  };

  return {
    users, setUsers,
    allUsers, setAllUsers,
    optionsForUsers, setOptionsForUsers,
    openUserRow, setOpenUserRow,
    searchUserText, setSearchUserText,
    searchText, setSearchText,
    searchUser, setSearchUser,
    userSearchText, setUserSearchText,
    validationErrors, setValidationErrors,
    userErrors, setUserErrors,
    passwordModalOpen, setPasswordModalOpen,
    passwordModalUserId, setPasswordModalUserId,
    passwordModalValues, setPasswordModalValues,
    passwordModalError, setPasswordModalError,
    deleteConfirmOpen, setDeleteConfirmOpen,
    deleteUserId,
    deleteUserName,
    confirmPasswords, setConfirmPasswords,
    form, setForm,
    openModalUser, setOpenModalForUser,
    editingUser, setEditingUser,
    addUserError, setAddUserError,
    userForms, setUserForms,
    userFormsErrors, setUserFormsErrors,
    emailDuplicateWarnings, setEmailDuplicateWarnings,
    expandedUserIndices,
    manualPassword, setManualPassword,
    showAddUserPassword, setShowAddUserPassword,
    userSubmitting,
    confirmSeatModal, setConfirmSeatModal,
    confirmSeatModalError,
    confirmSeatLoading,
    proratedAmount,
    isPremium, setIsPremium,
    manualSeatCount,
    isUserCardExpanded,
    toggleUserCard,
    addUserEntry,
    removeUserEntry,
    updateUserEntry,
    checkEmailDuplicate,
    handleUserInputChange,
    handleSaveUser,
    handleSetPassword,
    handleExpandUser,
    handleSearchUser,
    handleOpenModal,
    handleChangeForUser,
    validateUserEntry,
    handleSubmitUser,
    handleConfirmSeatAddition,
    handleResendInvite,
    handleDeleteUser,
    confirmDeleteUser,
    fetchUsers,
    fetchProratedAmount,
  };
}
